import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Table, Modal, Button, Flex, Form, Input, message, Alert, Descriptions, Space, Row, Col, Typography } from "antd";
import APIService from "../../services/APIService";
import { connect } from "react-redux";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";

function Server({ permission, user, isLoading }) {
    const apiService = useMemo(() => new APIService({ baseURL: process.env.REACT_APP_ADMIN_API_URL, product: 'MAKE_MY_PROFIT' }), []);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const [dataList, setDataList] = useState([]);
    const [detailModal, setDetailModal] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [searchText, setSearchText] = useState("");

    // Memoize permission check
    const canEdit = useMemo(() => permission?.server_permission === "EDIT" || user?.role !== "SUBADMIN", [permission, user]);
    const canView = useMemo(() => canEdit || permission?.server_permission === "VIEW", [canEdit, permission]);

    // Memoize columns
    const columns = useMemo(() => [
        { title: "#", dataIndex: "index", key: "index" },
        { title: "VPS IP", dataIndex: "vpsip", key: "vpsip" },
        Table.EXPAND_COLUMN,
        { title: "User Name", key: "username", dataIndex: "username" },
        { title: "Password", key: "Password", dataIndex: "password" },
        { title: "Total Account", key: "noOfAcc", dataIndex: "noOfAcc" },
        {
            title: "Action",
            key: "view",
            dataIndex: "restData",
            render: (val) => (
                <Space>
                    <Button type="text" onClick={() => viewClickedHandle(val)}><EyeOutlined /></Button>
                    <Button type="text" onClick={() => editClickedHandle(val)}><EditOutlined /></Button>
                    <Button type="text" onClick={() => deleteHandle(val)} danger><DeleteOutlined /></Button>
                </Space>
            ),
            width: 100
        },
    ], []);

    // Memoize expanded row render
    const expandedRowRender = useCallback((rec) => {
        const col = [
            { title: "#", dataIndex: "index", key: "index" },
            { title: "Account Name", dataIndex: "accName", key: "accName" },
            { title: "Email", dataIndex: "email", key: "email" },
            { title: "Full Name", key: "fullName", dataIndex: "fullName" }
        ];
        
        const recDoc = rec.restData.users || [];
        const data = recDoc.map((item, i) => ({
            index: i + 1,
            key: item._id,
            accName: item.accountName,
            email: item?.email,
            fullName: item?.fullName,
        }));
        return (
            <Table
                columns={col}
                dataSource={data}
                pagination={false}
                className="mb-3 mt-3 mr-3 inner-table"
            />
        );
    }, []);

    // Fetch server list
    const fetchListServer = useCallback(() => {
        apiService.get('/server/detail-list')
            .then(res => {
                const data = (res.data || []).map((dt, i) => ({
                    key: dt._id,
                    index: i + 1,
                    vpsip: dt.vps_ip,
                    username: dt.username,
                    password: dt.password,
                    restData: dt,
                    noOfAcc: dt?.users?.length || 0,
                    ibPassword: dt?.ib_password,
                    portalPassword: dt?.portal_password
                }));
                setDataList(data);
            })
            .catch(e => messageApi.open({ type: 'error', content: 'Failed to fetch server list' }));
    }, [apiService, messageApi]);

    useEffect(() => {
        fetchListServer();
    }, [fetchListServer]);

    // Modal open/close handlers
    const showModal = useCallback(() => {
        setIsModalOpen(true);
        setIsEdit(false);
        setViewData(null);
        form.resetFields();
    }, [form]);

    const showEditModal = useCallback((val) => {
        setIsModalOpen(true);
        setIsEdit(true);
        setViewData(val);
        form.setFieldsValue({
            acName: val.acName,
            acNumber: val.acNumber,
            brokerName: val.brokerName,
            vpsip: val.vps_ip,
            username: val.username,
            password: val.password,
            IBPassword: val.ib_password,
            portalPassword: val.portal_password
        });
    }, [form]);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setIsEdit(false);
        setViewData(null);
        form.resetFields();
    }, [form]);

    // View details modal
    const viewClickedHandle = useCallback((val) => {
        setViewData(val);
        setDetailModal(true);
    }, []);
    const modalCloseHandle = useCallback(() => {
        setDetailModal(false);
        setViewData(null);
    }, []);

    // Edit handler
    const editClickedHandle = useCallback((val) => {
        showEditModal(val);
    }, [showEditModal]);

    // Delete handler
    const deleteHandle = useCallback((data) => {
        apiService.delete(`/server/delete/${data._id}`)
            .then(() => {
                messageApi.open({ type: 'success', content: 'Server Deleted' });
                fetchListServer();
            })
            .catch(() => messageApi.open({ type: 'error', content: 'Failed to delete server' }));
    }, [apiService, fetchListServer, messageApi]);

    // Form submit (create or update)
    const handleFormSubmit = useCallback((val) => {
        const payload = {
            password: val?.password,
            username: val?.username,
            vpsIp: val?.vpsip,
            acName: val?.acName,
            acNumber: val?.acNumber,
            brokerName: val?.brokerName,
            iBPassword: val.IBPassword,
            portalPassword: val.portalPassword
        };
        if (isEdit && viewData) {
            apiService.put(`/server/update/${viewData._id}`, {
                ...payload,
                vps_ip: val?.vpsip // for update, API expects vps_ip
            })
                .then(() => {
                    messageApi.open({ type: 'success', content: 'Changes Updated' });
                    closeModal();
                    fetchListServer();
                })
                .catch(e => messageApi.open({ type: 'error', content: e.message || 'Update failed' }));
        } else {
            apiService.post("/server/create", payload)
                .then(() => {
                    messageApi.open({ type: 'success', content: 'New Server Created' });
                    closeModal();
                    fetchListServer();
                })
                .catch(() => messageApi.open({ type: 'error', content: 'Something went wrong' }));
        }
    }, [apiService, isEdit, viewData, messageApi, closeModal, fetchListServer]);

    // Search filter
    const filteredData = useMemo(() => {
        if (!searchText) return dataList;
        return dataList.filter(dt => dt.vpsip.toLowerCase().includes(searchText.toLowerCase()));
    }, [searchText, dataList]);

    return (
        <>
            {contextHolder}
            <Modal
                title={isEdit ? "Update Server" : "Create Server"}
                open={isModalOpen}
                onCancel={closeModal}
                footer={[]}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFormSubmit}
                    initialValues={isEdit && viewData ? {
                        acName: viewData.acName,
                        acNumber: viewData.acNumber,
                        brokerName: viewData.brokerName,
                        vpsip: viewData.vps_ip,
                        username: viewData.username,
                        password: viewData.password,
                        IBPassword: viewData.ib_password,
                        portalPassword: viewData.portal_password
                    } : {}}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Account Name" name={"acName"} rules={[{required: true,message: "Please enter Account Name"}]}>
                                <Input placeholder="Enter Account Name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Account Number" name={"acNumber"} rules={[{required: true,message: "Please enter Account Number"}]}>
                                <Input placeholder="Enter Account Number" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Broker Name" name={"brokerName"} rules={[{required: true,message: "Please enter Broker Number"}]}>
                                <Input placeholder="Enter Broker Name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="IB Password" name={"IBPassword"}>
                                <Input placeholder="Enter Broker Name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="VPS IP" name={"vpsip"} rules={[{required: true,message: "Please enter VPS IP"}]}>
                                <Input placeholder="Enter VPS IP" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Portal Password" name={"portalPassword"}>
                                <Input placeholder="Enter VPS IP" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="User Name" name={"username"} rules={[{required: true,message: "Please enter User Name"}]}>
                                <Input placeholder="Enter UserName" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Password" name={"password"} rules={[{required: true,message: "Please enter Password!"}]}>
                                <Input placeholder="Enter Password" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Button loading={isLoading} type="primary" htmlType="submit">Submit</Button>
                </Form>
            </Modal>
            <Modal title="Server Details" footer={[<Button onClick={modalCloseHandle} type="primary">OK</Button>]} open={detailModal} onOk={modalCloseHandle} onCancel={modalCloseHandle}>
                <Descriptions bordered>
                    <Descriptions.Item span={3} label={"Account Name"}>{viewData?.acName}</Descriptions.Item>
                    <Descriptions.Item span={3} label={"Account Number"}>{viewData?.acNumber}</Descriptions.Item>
                    <Descriptions.Item span={3} label={"Broker Name"}>{viewData?.brokerName}</Descriptions.Item>
                    <Descriptions.Item span={3} label={"VPS IP"}>{viewData?.vps_ip}</Descriptions.Item>
                    <Descriptions.Item span={3} label={"User Name"}>{viewData?.username}</Descriptions.Item>
                    <Descriptions.Item span={3} label={"Password"}>{viewData?.password}</Descriptions.Item>
                </Descriptions>
            </Modal>
            <Typography.Title level={3} className="page-title">Server</Typography.Title>
            <Flex justify="end" style={{ marginBottom: "20px" }}>
                <Input style={{ marginRight: 5, maxWidth: 200 }} placeholder="Search by VPS IP..." onChange={e => setSearchText(e.target.value)} />
                {canEdit && <Button type="primary" onClick={showModal}>Create Server</Button>}
            </Flex>
            {canView ? (
                <Table
                    loading={isLoading}
                    columns={columns}
                    expandable={{ expandedRowRender, rowExpandable: rec => rec?.restData?.users?.length > 0 }}
                    dataSource={filteredData}
                />
            ) : (
                <Alert message="You havn't Enough Permission" />
            )}
        </>
    );
}

const mapStateToProps = ({ login, loading }) => {
    const { permission, user } = login;
    const { isLoading } = loading;
    return { permission, user, isLoading };
};
export default connect(mapStateToProps, {})(Server);